// ─────────────────────────────────────────────────────────────────────────────────────────────
// COPIE VERSIONNÉE ET ADAPTÉE POUR TRAME.
// Origine   : dépôt VLT Devis, public/outils/metiers/cvc/chaud.mjs
//             (C:\Users\wecha\vlt-devis-saas\public\outils\metiers\cvc\chaud.mjs)
// Version   : commit 53f27dce35157919e1bdb92bf9dcd6c1e135e83c du 18/09/2026, fichier du 18/09/2026 17:59
// Empreinte : SHA-256 du source 36E582E7284D403F07BE6929461715C4F628BA27FB034A5E28A4C77A29F7F370
// Copie     : 07/10/2026. Déperditions et température de base INCHANGÉES (tests 12 à 15 de l'origine, mêmes valeurs) ;
//             le refus sans département ni altitude (F14) et sans source pour une valeur forcée (F06) est MAINTENU.
// ADAPTATION TRAME (recette du 06/10/2026, § 9.4 — À VALIDER PAR LE DIRIGEANT) :
//   A4. puissanceEmetteur() : puissance réelle d'un émetteur à eau (radiateur) à un régime basse température, par la loi
//       des émetteurs P = P_nominale × (ΔT / ΔT_nominal)^n. Exposant n paramétrable ; défaut 1,3 documenté « à valider ».
//       Régime nominal par défaut 75/65/20 °C (ΔT 50 K, conditions d'essai usuelles des catalogues) ; écart de
//       température moyen arithmétique par défaut, logarithmique au choix. Données incohérentes : refus, pas de valeur.
// ─────────────────────────────────────────────────────────────────────────────────────────────
// Assistant Climatisation CVC — bilan CHAUD : déperditions simplifiées. Moteur PUR.
//
// Méthode INSPIRÉE de la NF EN 12831 (norme payante, non reproduite), telle que fixée par le
// cahier des charges (docs/mes-outils/cahiers/CDC-CVC.md §3.2). Ce n'est PAS un calcul
// réglementaire et cela ne remplace pas une étude thermique.
//
//   Phi_HL = (H_T + H_V) x (theta_int - theta_e)      puis  Phi_dim = Phi_HL x (1 + securite)
//   H_T    = Somme_k U_k x A_k x b_k  +  dU_TB x Somme_k (A_k x b_k)
//   H_V    = 0,34 x Vpoint   avec Vpoint = Vpoint_vent x (1 - rendement) + n_inf x S x h
//
// La température extérieure de base theta_e vient de la table publique du §18.1 de la méthode
// 3CL-DPE 2021 (arrêté du 31 mars 2021), portée dans zones-climatiques.ts. Département inconnu
// ou altitude absente : le calcul chaud est REFUSÉ (F14) — on n'invente pas une température de
// base, une valeur fausse ferait sous-dimensionner un chauffage chez un client.
//
// Aucune donnée constructeur, aucun prix, aucune donnée d'entreprise ici.

import { U_ISOLATION, TYPES_PIECE, surfaceVitrages, geometrie, U_VITRAGE, type AlerteCvc, type Piece } from './froid.ts';
import { zoneDuDepartement, THETA_E, TRANCHES_ALTITUDE, type ZoneClimatique } from './zones-climatiques.ts';

const n = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) ? x : null);

/* --------------------------- hypothèses logiciel (modifiables) --------------------------- */

// Coefficients de réduction b des parois en contact avec un espace non chauffé (hypothèses
// logiciel, ordres de grandeur de la méthode simplifiée). CDC §3.2.
export const B_PAROI = Object.freeze({
  exterieur: 1,
  toiture: 1,
  plafond_combles: 0.9,
  plancher_vide_sanitaire: 0.8,
  plancher_local_non_chauffe: 0.6,
  plancher_terre_plein: 0.5,
  mur_local_non_chauffe: 0.6,
  local_chauffe: 0,
});

export const PLANCHERS_BAS = Object.freeze([
  { id: 'local_chauffe', nom: 'Sur local chauffé', b: 0 },
  { id: 'terre_plein', nom: 'Sur terre-plein', b: 0.5 },
  { id: 'vide_sanitaire', nom: 'Sur vide sanitaire', b: 0.8 },
  { id: 'local_non_chauffe', nom: 'Sur local non chauffé (cave, garage)', b: 0.6 },
]);

// Ponts thermiques forfaitaires (W/m².K), rapportés à la surface déperditive pondérée.
export const PONTS_THERMIQUES = Object.freeze([
  { id: 'standard', nom: 'Cas courant', valeur: 0.10 },
  { id: 'ite', nom: 'Isolation par l’extérieur', valeur: 0.05 },
  { id: 'iti_non_traites', nom: 'Isolation intérieure, planchers et refends non traités', valeur: 0.15 },
]);

// Taux d'infiltration par défaut selon la période (vol/h). Même valeur qu'en été : une seule
// saisie, pas de double comptage.
export const INFILTRATIONS = Object.freeze([
  { id: 'avant_1975', nom: 'Avant 1975, menuiseries d’origine', valeur: 0.5 },
  { id: 'menuiseries_remplacees', nom: 'Menuiseries remplacées, ou 1975-2012', valeur: 0.3 },
  { id: 'apres_2012', nom: 'Après 2012', valeur: 0.15 },
]);

export const REGLAGES_CHAUD = Object.freeze({
  pont_thermique_w_m2k: 0.10,
  capacite_air_wh_m3k: 0.34,   // rho x c de l'air
  securite_pct: 10,   // coefficient de sécurité sur le besoin (ce n'est PAS une marge commerciale)
  recuperation_pct: 0,         // rendement d'une VMC double flux : 0 tant qu'il n'est pas saisi
});
export type ReglagesChaud = { -readonly [K in keyof typeof REGLAGES_CHAUD]: number };

export { THETA_E, TRANCHES_ALTITUDE };

/* ---------------------- température extérieure de base (theta_e) ---------------------- */

export type TemperatureBase =
  | { refus: true; code: 'F06' | 'F14'; message: string }
  | { refus?: undefined; theta_e: number; zone: ZoneClimatique | null; tranche: number | null; altitude_m?: number; origine: 'forcee' | 'table_3cl'; source: string };

/**
 * Résout theta_e. Renvoie { theta_e, zone, tranche, origine } ou { refus, code, message }.
 * Aucune valeur n'est devinée : sans département + altitude, ni valeur forcée sourcée, on refuse.
 */
export function temperatureDeBase({ departement, altitude_m, theta_e_force, source }:
  { departement?: unknown; altitude_m?: unknown; theta_e_force?: unknown; source?: unknown } = {}): TemperatureBase {
  const force = n(theta_e_force);
  if (force != null) {
    // F06 : une valeur forcée sans source documentée est refusée.
    if (!String(source || '').trim()) {
      return { refus: true, code: 'F06', message: 'Température extérieure de base forcée sans source : indiquez la source (norme, relevé, notice) ou laissez la valeur de la table publique.' };
    }
    return { theta_e: force, zone: null, tranche: null, origine: 'forcee', source: String(source).trim() };
  }
  const zone = zoneDuDepartement(departement);
  const alt = n(altitude_m);
  if (!zone || alt == null) {
    return {
      refus: true, code: 'F14',
      message: 'Calcul chaud impossible : renseignez le département et l’altitude du chantier (ou forcez la température extérieure de base avec sa source).',
    };
  }
  const tranche = alt < 400 ? 0 : alt < 800 ? 1 : 2;
  const groupe = zone.startsWith('H1') ? 'H1' : zone.startsWith('H2') ? 'H2' : 'H3';
  return {
    theta_e: THETA_E[groupe][tranche],
    zone, tranche, altitude_m: alt, origine: 'table_3cl',
    source: 'Arrêté du 31 mars 2021 (méthode 3CL-DPE 2021), annexe 1 §18.1 — zone ' + zone + ', ' + TRANCHES_ALTITUDE[tranche] + '.',
  };
}

/* --------------------------- déperditions d'une pièce --------------------------- */

export interface ParoiChaud { nom: string; u: number; surface_m2: number; b: number; h_w_k: number }

export interface ResultatChaud {
  id: string; nom: string; count: number; manques?: string[]; refus?: string; message?: string; erreur?: boolean;
  hypotheses?: string[]; alertes?: AlerteCvc[];
  surface_m2?: number; theta_int_c?: number; theta_e_c?: number; delta_t_k?: number;
  parois?: ParoiChaud[]; h_t_w_k?: number; h_t_parois_w_k?: number; h_t_ponts_w_k?: number; surface_deperditive_ponderee_m2?: number;
  h_v_w_k?: number; debit_air_m3h?: number; phi_w?: number; dimensionnement_w?: number; ratio_w_m2?: number;
  /** P0-1 — part de l'air neuf prétraité comptée dans la pièce (soufflage sous la consigne), W ; absent sans prétraitement. */
  air_neuf_pretraite_w?: number;
  /** P0-1 — charge de l'air neuf jusqu'à l'état de soufflage, à reprendre par la CTA (null sans prétraitement). */
  air_neuf_cta?: { puissance_w: number; debit_m3h: number; soufflage_t: number } | null;
}

/**
 * Déperditions d'UNE pièce (un exemplaire). `base` = résultat de temperatureDeBase().
 * Retourne { manques } ou { refus } plutôt qu'un chiffre faux.
 */
export function calculerPiece(piece: Piece, base: TemperatureBase, reglages: Partial<ReglagesChaud> = REGLAGES_CHAUD): ResultatChaud {
  const R: ReglagesChaud = { ...REGLAGES_CHAUD, ...reglages };
  const hypotheses: string[] = [];
  const alertes: AlerteCvc[] = [];
  if (base && base.refus) return { id: piece.id, nom: piece.nom, count: n(piece.count) || 1, refus: base.code, message: base.message };
  const manques: string[] = [];
  if (n(piece.surface_m2) == null && !(n(piece.longueur_m) && n(piece.largeur_m))) manques.push('surface');
  if (n(piece.hauteur_m) == null) manques.push('hauteur');
  // P0-1 : un air neuf « prétraité » sans température de soufflage n'est pas calculé (on ne devine pas la sortie de la CTA).
  const pretraite = piece.air_neuf_pretraite === true && (n(piece.ventilation_m3h) || 0) > 0;
  if (pretraite && n(piece.soufflage_t) == null) manques.push('soufflage');
  if (manques.length) return { id: piece.id, nom: piece.nom, count: n(piece.count) || 1, manques, hypotheses, alertes };

  const g = geometrie(piece, hypotheses);
  if (!g) return { id: piece.id, nom: piece.nom, count: n(piece.count) || 1, manques: ['surface'], hypotheses, alertes };

  const type = TYPES_PIECE.find((t) => t.id === piece.type);
  const thetaInt = n(piece.theta_int_c) != null ? n(piece.theta_int_c)! : (type ? type.theta : 20);
  if (n(piece.theta_int_c) == null) hypotheses.push('Température intérieure de confort ' + thetaInt + ' °C pour « ' + (type ? type.nom : 'la pièce') + ' » (hypothèse logiciel).');

  const u = U_ISOLATION[piece.isolation ?? ''] || U_ISOLATION.inconnue;
  const uMur = n(piece.u_mur) != null ? n(piece.u_mur)! : u[0];
  const uToit = n(piece.u_toiture) != null ? n(piece.u_toiture)! : u[1];
  const uPlancher = n(piece.u_plancher) != null ? n(piece.u_plancher)! : u[2];
  const uVitre = n(piece.u_vitrage) != null ? n(piece.u_vitrage)! : (U_VITRAGE[piece.vitrage_defaut ?? ''] || U_VITRAGE.double);
  const uPorte = n(piece.porte_u) != null ? n(piece.porte_u)! : 2;

  const sVitres = surfaceVitrages(piece.vitrages);
  const sPortes = n(piece.portes_m2) || 0;
  const brute = n(piece.facade_brute_m2) != null ? n(piece.facade_brute_m2)! : g.facade_brute_m2;
  const sMurs = Math.max(0, brute - sVitres - sPortes);
  const plancher = PLANCHERS_BAS.find((p) => p.id === piece.plancher_bas) || PLANCHERS_BAS[0];
  const sMurLnc = n(piece.mur_local_non_chauffe_m2) || 0;

  const parois = ([
    ['Murs extérieurs', uMur, sMurs, B_PAROI.exterieur],
    ['Vitrages', uVitre, sVitres, B_PAROI.exterieur],
    ['Portes', uPorte, sPortes, B_PAROI.exterieur],
    ['Toiture', uToit, piece.position === 'sous_toiture' ? g.surface : 0, B_PAROI.toiture],
    ['Plafond sous combles', uToit, piece.position === 'sous_combles' ? g.surface : 0, B_PAROI.plafond_combles],
    ['Plancher bas ' + plancher.nom.toLowerCase(), uPlancher, plancher.b > 0 ? g.surface : 0, plancher.b],
    ['Murs sur local non chauffé', uMur, sMurLnc, B_PAROI.mur_local_non_chauffe],
  ] as [string, number, number, number][]).filter((p) => p[2] > 0 && p[3] > 0);

  const surfacePonderee = parois.reduce((s, p) => s + p[2] * p[3], 0);
  const dUtb = n(piece.pont_thermique_w_m2k) != null ? n(piece.pont_thermique_w_m2k)! : R.pont_thermique_w_m2k;
  const htParois = parois.reduce((s, p) => s + p[1] * p[2] * p[3], 0);
  const htPonts = dUtb * surfacePonderee;
  const ht = htParois + htPonts;
  if (surfacePonderee > 0) hypotheses.push('Ponts thermiques forfaitaires : ' + String(dUtb).replace('.', ',') + ' W/m².K rapportés aux surfaces déperditives.');

  const vent = n(piece.ventilation_m3h) || 0;
  const recup = n(piece.recuperation_pct) != null ? n(piece.recuperation_pct)! : R.recuperation_pct;
  const infil = n(piece.infiltration_volh);
  if (infil == null) hypotheses.push('Taux d’infiltration non renseigné : infiltrations non comptées en chaud non plus.');
  const debitInfil = infil == null ? 0 : infil * g.surface * n(piece.hauteur_m)!;
  const dt = thetaInt - base.theta_e;
  let debit: number;
  let hv: number;
  let phiAirNeufPretraite = 0;
  let airNeufCta: ResultatChaud['air_neuf_cta'] = null;
  if (!pretraite) {
    // Calcul d'ORIGINE, inchangé.
    if (recup === 0) hypotheses.push('Aucune récupération de chaleur sur l’air neuf : saisissez le rendement pour une VMC double flux.');
    debit = vent * (1 - recup / 100) + debitInfil;
    hv = R.capacite_air_wh_m3k * debit;
  } else {
    // P0-1 (22/10/2026), même règle que l'adaptation A2 du froid : l'air neuf prétraité entre à sa température de soufflage ;
    // seule la différence avec la consigne de chauffage est comptée dans le besoin de la pièce. Les infiltrations restent à
    // l'état extérieur. La charge de l'air neuf jusqu'à l'état de soufflage revient à la batterie chaude de la CTA (sortie à part).
    const soufflage = n(piece.soufflage_t)!;
    debit = debitInfil;
    hv = R.capacite_air_wh_m3k * debitInfil;
    const brut = R.capacite_air_wh_m3k * vent * (thetaInt - soufflage);
    phiAirNeufPretraite = Math.max(0, brut);
    if (brut < 0) hypotheses.push('Air neuf prétraité soufflé au-dessus de la consigne : apport de chaleur non déduit (pré-dimensionnement prudent).');
    const recupCta = recup > 0 ? recup : 0;
    const cta = R.capacite_air_wh_m3k * vent * (1 - recupCta / 100) * Math.max(0, soufflage - base.theta_e);
    airNeufCta = { puissance_w: cta, debit_m3h: vent, soufflage_t: soufflage };
    hypotheses.push('Air neuf prétraité par une CTA : soufflé à ' + String(soufflage).replace('.', ',') + ' °C (état saisi) ; en chaud, sa charge jusqu’à cet état est à reprendre par la CTA'
      + (recupCta > 0 ? ' (récupération de ' + String(recupCta).replace('.', ',') + ' % déduite)' : ' (aucune récupération saisie)') + ', elle ne compte pas dans le besoin des unités terminales.');
  }

  const phi = (ht + hv) * dt + phiAirNeufPretraite;
  const securite = n(R.securite_pct) == null ? 10 : R.securite_pct;
  const ratio = phi / g.surface;

  if (!parois.length) {
    alertes.push({ code: 'F05', niveau: 'attention', cible: piece.id, message: piece.nom + ' : aucune paroi déperditive ni vitrage — le besoin de chauffage se limite au renouvellement d’air.' });
  }
  if (ratio > 200 || ratio < 20) {
    alertes.push({ code: 'F02', niveau: 'attention', cible: piece.id, message: piece.nom + ' : déperditions de ' + Math.round(ratio) + ' W/m² ' + (ratio > 200 ? '(au-delà de 200)' : '(sous 20)') + ' — vérifiez parois, vitrages et débits.' });
  }
  if (piece.intermittence) {
    alertes.push({ code: 'F15', niveau: 'attention', cible: piece.id, message: piece.nom + ' : occupation intermittente ou résidence secondaire — prévoyez une puissance de relance, non calculée par l’outil.' });
  }

  return {
    id: piece.id, nom: piece.nom, count: n(piece.count) || 1, manques: [],
    surface_m2: g.surface,
    theta_int_c: thetaInt, theta_e_c: base.theta_e, delta_t_k: dt,
    parois: parois.map((p) => ({ nom: p[0], u: p[1], surface_m2: p[2], b: p[3], h_w_k: p[1] * p[2] * p[3] })),
    h_t_w_k: ht, h_t_parois_w_k: htParois, h_t_ponts_w_k: htPonts, surface_deperditive_ponderee_m2: surfacePonderee,
    h_v_w_k: hv, debit_air_m3h: debit,
    ...(pretraite ? { air_neuf_pretraite_w: phiAirNeufPretraite, air_neuf_cta: airNeufCta } : {}),
    phi_w: phi,
    dimensionnement_w: phi * (1 + securite / 100),
    ratio_w_m2: ratio,
    hypotheses: [...new Set(hypotheses)], alertes,
  };
}

/* ------------------- A4 — puissance réelle d'un émetteur en basse température ------------------- */

/** Régime d'essai des catalogues de radiateurs (départ / retour / ambiance, °C) : ΔT 50 K. Modifiable. */
export const REGIME_NOMINAL_EMETTEUR = Object.freeze({ depart_c: 75, retour_c: 65, ambiance_c: 20 });

/**
 * Exposant de la loi des émetteurs. À VALIDER : la valeur exacte de chaque modèle figure sur la fiche du fabricant
 * (essai NF EN 442). 1,3 est la valeur usuelle d'un radiateur à panneaux, retenue par l'analyse experte du dossier de recette.
 */
export const EXPOSANT_EMETTEUR_DEFAUT = 1.3;
export const SOURCE_EXPOSANT_EMETTEUR = 'Exposant n = 1,3 : valeur usuelle d’un radiateur à panneaux, hypothèse à valider — la valeur exacte figure sur la fiche du fabricant (essai NF EN 442).';
export const BORNES_EXPOSANT_EMETTEUR = Object.freeze([1, 2] as const);

export type MoyenneEmetteur = 'arithmetique' | 'logarithmique';

export type PuissanceEmetteur =
  | { refus: true; code: 'E00' | 'E01'; message: string }
  | { refus?: undefined; puissance_w: number; facteur: number; delta_t_k: number; delta_t_nominal_k: number; exposant: number;
      moyenne: MoyenneEmetteur; hypotheses: string[] };

const ecartMoyen = (dep: number, ret: number, amb: number, moyenne: MoyenneEmetteur) => {
  if (moyenne === 'arithmetique' || Math.abs(dep - ret) < 1e-9) return (dep + ret) / 2 - amb;
  return (dep - ret) / Math.log((dep - amb) / (ret - amb));
};

/**
 * Puissance d'un émetteur à eau à un régime donné : P = P_nominale × (ΔT / ΔT_nominal)^n.
 * Exemple de contrôle (recette, constat n° 9) : radiateur 75/65/20 → 45/40/20, n = 1,3 : (22,5 / 50)^1,3 ≈ 0,354.
 * Refus (jamais une valeur) si la puissance nominale manque ou si un régime est incohérent.
 */
export function puissanceEmetteur({
  nominale_w, depart_c, retour_c, ambiance_c = 20, exposant = EXPOSANT_EMETTEUR_DEFAUT,
  nominal = REGIME_NOMINAL_EMETTEUR, moyenne = 'arithmetique',
}: {
  nominale_w: unknown; depart_c: unknown; retour_c: unknown; ambiance_c?: unknown; exposant?: unknown;
  nominal?: { depart_c: number; retour_c: number; ambiance_c: number }; moyenne?: MoyenneEmetteur;
}): PuissanceEmetteur {
  const pn = n(nominale_w);
  const td = n(depart_c);
  const tr = n(retour_c);
  const ta = n(ambiance_c);
  const ex = n(exposant);
  if (pn == null || pn <= 0 || td == null || tr == null || ta == null) {
    return { refus: true, code: 'E00', message: 'Puissance de l’émetteur non calculée : renseignez sa puissance nominale et les températures de départ, de retour et d’ambiance.' };
  }
  if (ex == null || ex < BORNES_EXPOSANT_EMETTEUR[0] || ex > BORNES_EXPOSANT_EMETTEUR[1]) {
    return { refus: true, code: 'E01', message: 'Exposant de l’émetteur attendu entre 1 et 2 (valeur de la fiche du fabricant).' };
  }
  const regimes = [[td, tr, ta], [nominal.depart_c, nominal.retour_c, nominal.ambiance_c]];
  if (regimes.some(([d, r, a]) => !(d >= r && r > a))) {
    return { refus: true, code: 'E01', message: 'Régime d’eau incohérent : il faut départ ≥ retour > ambiance (au régime retenu comme au régime nominal).' };
  }
  const dT = ecartMoyen(td, tr, ta, moyenne);
  const dTn = ecartMoyen(nominal.depart_c, nominal.retour_c, nominal.ambiance_c, moyenne);
  const facteur = (dT / dTn) ** ex;
  const fr = (x: number) => String(Math.round(x * 100) / 100).replace('.', ',');
  return {
    puissance_w: pn * facteur, facteur, delta_t_k: dT, delta_t_nominal_k: dTn, exposant: ex, moyenne,
    hypotheses: [
      'Émetteurs : P = P_nominale × (ΔT / ΔT_nominal)^n, régime ' + fr(td) + '/' + fr(tr) + '/' + fr(ta) + ' °C contre ' + fr(nominal.depart_c) + '/' + fr(nominal.retour_c) + '/' + fr(nominal.ambiance_c)
        + ' °C nominal, écart moyen ' + (moyenne === 'arithmetique' ? 'arithmétique' : 'logarithmique') + ', n = ' + fr(ex) + ' → ' + fr(facteur * 100) + ' % de la puissance nominale.',
      ex === EXPOSANT_EMETTEUR_DEFAUT ? SOURCE_EXPOSANT_EMETTEUR : 'Exposant n = ' + fr(ex) + ' saisi (fiche du fabricant).',
    ],
  };
}
